import React, { useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import { Card, PagingQuery } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import MetricsContent from './MetricsContent';
import MetricsHeader from './MetricsHeader';
import CheckboxHeader from './CheckboxHeader';
import CheckboxContent from './CheckboxContent';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import './index.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IListDataItem } from '../../types';
import { PER_TYPES, TABLE_FIRST_COLUMN_KEYS } from '../../constants';
import { addFormatMessageSafelyHandler } from '../../utils';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';

interface ITableProps extends IBaseProps {
  isMobile: boolean;
  isLoading: boolean;
  isDodHide: boolean;
  isTableCheckboxHide: boolean;
  isRegionPage: boolean;
  records: IListDataItem[];
  pageInfo: any;
  pageSizeOptions: number[];
  total: number;
  pageIndex: number;
  pageSize: number;
  sortKey: string;
  sortOrder: 'desc' | 'asc';
  tableSelected: IListDataItem[];
  tableSelectedAll: IListDataItem[];
  onTablePagingChange: Function;
  onTableSortChage: Function;
  onTableCheckboxChange: Function;
  tableMetricsColumns: {
    title: string; // tableMetricsColumns以前的值
    simpleTitle: string;
  }[];
}

const METRICS_COLUMN_WIDTH = '100%';
const UNIQUE_KEY = 'name';
export default function Table(props: ITableProps) {
  const {
    isMobile,
    isLoading,
    isDodHide,
    isTableCheckboxHide,
    isTableNameCanBeClick,
    records = [],
    pageInfo,
    pageSizeOptions,
    total,
    pageIndex,
    pageSize,
    sortKey,
    tableMetricsColumns,
    sortOrder,
    tableSelected,
    tableSelectedAll,
    onTablePagingChange,
    onTableSortChage,
    onTableCheckboxChange,
    onNameClick
  } = props;

  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const onPagingChange = (e: PagingQuery) => {
    onTablePagingChange(e);
  };

  const onSortChange = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  const getColumns = () => {
    const getMetricCloumns = () =>
      tableMetricsColumns.map((nameData: any, index:number) => {
        const { title: name, simpleTitle: simpleName } = nameData || {};
        return {
          key: name,
          header: (
            <>
              <MetricsHeader
                key={name}
                title={name}
                simpleTitle={simpleName}
                sortKey={name}
                defaultSortKey={sortKey}
                defaultSortOrder={sortOrder}
                isDodHide={isDodHide}
                onSortChange={onSortChange}
                width={METRICS_COLUMN_WIDTH}
              />
            </>
          ),
          render: (cvm: any) => {
            const { metrics = [] } = cvm || {};
            const { metric, metric_cn: metricCN, Value = '-', dod, metric_abbr } = metrics?.[index] || [];
            return (
              <MetricsContent
                key={name}
                metric={metric}
                metricCN={metricCN}
                value={Value}
                dod={dod}
                isDodHide={isDodHide}
                width={METRICS_COLUMN_WIDTH}
                isTextCanBeClick = {isTableNameCanBeClick(name, 'metric')}
                onTextClick={() => {
                  onNameClick({
                    name: metric,
                    name_abbr: metric_abbr
                  }, 'metric');
                }}
              />
            );
          },
        };
      });
    const columns = [
      {
        key: pageInfo?.name,
        header: (
          <>
            <div className="dbi-FirstRowTable-flex">
              <div className="dbi-text-id">#</div>
              <div>{formatMessageSafely({ id: TABLE_FIRST_COLUMN_KEYS[pageInfo?.name] }) ?? pageInfo?.name}</div>
            </div>
          </>
        ),
        width: 330,
        fixed: 'left',
        render: (cvm: any) => {
          const { name, name_cn: nameCN, name_abbr: nameAbbr, rank, icon } = cvm || {};
          const isGlobal = name?.toLowerCase() === 'global' || nameAbbr?.toLowerCase() === 'global';
          const nameText = (isCN ? nameCN : name) || name;
          return (
            <div className="dbi-FirstRowTable-flex" key={name}>
              <div className="dbi-text-id">{isMobile? (pageIndex -1) * pageSize + rank + 1 : rank + 1}</div>
              <div className="dbi-region-country-wrapper">
                {icon && <img className={classnames('dbi-region-country-icon', { 'is-global-icon': isGlobal })} src={icon} alt="" />}
                <span onClick={() => onNameClick(cvm, 'name')} className={isTableNameCanBeClick(name, 'name') ? 'hover' : ''}>{nameText}</span>
              </div>
            </div>
          );
        },
      },
      ...getMetricCloumns(),
    ];
    if (!isTableCheckboxHide) {
      columns.unshift({
        key: 'select',
        width: 47,
        fixed: 'left',
        header: (
          <CheckboxHeader
            selected={tableSelected}
            selectedAll={tableSelectedAll}
            uniqueKey={UNIQUE_KEY}
            disabled={isLoading || records.length === 0}
            onChange={(list: any, status: string) => {
              onTableCheckboxChange(list, status);
            }}
            />
        ),
        render: (cvm: any) => (
          <CheckboxContent
            selected={tableSelected}
            context={cvm}
            uniqueKey={UNIQUE_KEY}
            onChange={(list: any, status: string) => {
              onTableCheckboxChange(list, status);
            }}
          />
        ),
      });
    }
    return columns;
  };

  return (
    <Card className='global-view-table-wrap'>
      <Card.Body>
        <Abnormal
          isLoading={isLoading}
          isNoData={!isLoading && !records?.length}
          component={
            <TopchartTable
              loading={false}
              records={records}
              tableTotal={total}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPagingChange={onPagingChange}
              pageSizeOptions={pageSizeOptions}
              className='global-view-table dbi-table-sticky'
              columns={getColumns()}
              scrollMaxHeight={'100%'}
            />
          }
        />
      </Card.Body>
    </Card>
  );
}
